import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { get } from "lodash";
import { sendChangeEmailOtp, sendChangePhoneOtp, verifySsoCode, putUserDetails } from "../../../helper/utils";
import { setUserDetails } from "../../../store/actions";
import "./index.m.css";
import { UserIcon, PhoneIcon, EmailIcon, TimerIcon, SpinnerIcon } from "./icons";

export const OnboardModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const reduxUserDetails = useSelector((state) => get(state, ["user", "user"], {}));
  const isGoogleUser = reduxUserDetails?.metadata?.login_method === "google_login" || reduxUserDetails?.metadata?.login_method === "google";
  const [isSubmittingOnboarding, setIsSubmittingOnboarding] = useState(false);

  // Robust fallback: if Redux is empty, check localStorage
  let userdetails = reduxUserDetails;

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  if (typeof window !== "undefined" && Object.keys(reduxUserDetails).length === 0) {
    try {
      const localUser = window.localStorage.getItem("user");
      if (localUser) {
        userdetails = JSON.parse(localUser);
      }
    } catch (e) {
      console.error(e);
    }
  }

  const userData = userdetails?.userData || userdetails || {};
  const userMetadata = userdetails?.metadata || {};

  const initialMobileRaw = userData?.phoneNumbers?.[0] || userdetails?.phoneNumber || "";
  let initialMobile = initialMobileRaw.replace(/^\+91/, "");
  if (initialMobile.length > 10 && initialMobile.startsWith("91")) {
    initialMobile = initialMobile.substring(2);
  }
  const initialEmail = userData?.emails?.[0] || userdetails?.email || "";
  const initialFirstName = userMetadata?.first_name || "";

  const [mobileNumber, setMobileNumber] = useState(initialMobile || "");
  const [savedMobile, setSavedMobile] = useState(initialMobile);
  const [mobileError, setMobileError] = useState(false);
  const [mobileErrorMessage, setMobileErrorMessage] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(30);
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState(false);
  const [otpVerified, setOtpVerified] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);

  const [name, setName] = useState(initialFirstName);
  const [savedName, setSavedName] = useState(initialFirstName);
  const [nameDone, setNameDone] = useState(true);
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState("");

  const [email, setEmail] = useState(initialEmail);
  const [savedEmail, setSavedEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailTimer, setEmailTimer] = useState(30);
  const [emailOtpValue, setEmailOtpValue] = useState("");
  const [emailOtpError, setEmailOtpError] = useState(false);
  const [emailOtpVerified, setEmailOtpVerified] = useState(true);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);

  const [authSessionInfo, setAuthSessionInfo] = useState({ preAuthSessionId: null, deviceId: null });
  const isMobileValid = !mobileNumber || mobileNumber.trim().length === 0 || otpVerified;
  const isEmailValid = !email || email.trim().length === 0 || emailOtpVerified;
  const hasAtLeastOneContact = (mobileNumber && mobileNumber.trim().length > 0) || (email && email.trim().length > 0);
  const isNameValidForSubmit = nameDone && savedName && savedName.trim().length >= 2 && !/^\d+$/.test(savedName.trim());
  const isFormComplete = isNameValidForSubmit && isMobileValid && isEmailValid && hasAtLeastOneContact;

  useEffect(() => {
    if (initialMobile && mobileNumber !== initialMobile && !savedMobile) {
      setMobileNumber(initialMobile);
      setSavedMobile(initialMobile);
      setOtpVerified(true);
    }
    if (initialEmail && email !== initialEmail && !savedEmail) {
      setEmail(initialEmail);
      setSavedEmail(initialEmail);
      setEmailOtpVerified(true);
    }
    if (initialFirstName && name !== initialFirstName && !savedName) {
      setName(initialFirstName);
      setSavedName(initialFirstName);
      setNameDone(true);
    }
  }, [initialMobile, initialEmail, initialFirstName]);

  useEffect(() => {
    let interval;
    if (otpSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  useEffect(() => {
    let interval;
    if (emailOtpSent && emailTimer > 0) {
      interval = setInterval(() => {
        setEmailTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [emailOtpSent, emailTimer]);

  const isNameChanged = name !== savedName;
  const isNameValid = name.trim().length >= 2 && !/^\d+$/.test(name.trim());
  const isNameUpdateDisabled = !isNameChanged || isUpdatingName;

  const closeOtherEdits = (currentField) => {
    if (currentField !== "name" && !nameDone) {
      setName(savedName || "");
      setNameDone(true);
      setNameError(false);
      setNameErrorMessage("");
    }
    if (currentField !== "mobile" && !otpVerified) {
      setMobileNumber(savedMobile || "");
      setOtpVerified(true);
      setOtpSent(false);
      setMobileError(false);
      setMobileErrorMessage("");
      setTimer(30);
      setOtpValue("");
    }
    if (currentField !== "email" && !emailOtpVerified) {
      setEmail(savedEmail || "");
      setEmailOtpVerified(true);
      setEmailOtpSent(false);
      setEmailError(false);
      setEmailErrorMessage("");
      setEmailTimer(30);
      setEmailOtpValue("");
    }
  };

  const handleNameBlur = () => {
    if (name) {
      setName(name.trim());
    }
    if (name && (name.trim().length < 2 || /^\d+$/.test(name.trim()))) {
      setNameError(true);
      setNameErrorMessage("Please enter at least 2 characters.");
    } else {
      setNameError(false);
    }
  };

  const handleUpdateName = () => {
    if (isUpdatingName) return;

    const trimmedName = name.trim();
    setName(trimmedName);

    if (trimmedName === savedName) {
      setNameDone(true);
      setNameError(false);
      setNameErrorMessage("");
      return;
    }

    if (trimmedName.length < 2) {
      setNameError(true);
      setNameErrorMessage("Please enter at least 2 characters.");
      return;
    }

    if (/\d/.test(trimmedName)) {
      setNameError(true);
      setNameErrorMessage("Please enter a valid name.");
      return;
    }

    setIsUpdatingName(true);
    setNameError(false);
    setNameErrorMessage("");
    putUserDetails({ first_name: trimmedName })
      .then((res) => {
        setIsUpdatingName(false);
        if (res && !res.errorMessage) {
          setSavedName(trimmedName);
          setNameDone(true);

          const updatedUser = { ...userdetails, metadata: { ...userdetails?.metadata, first_name: trimmedName } };
          dispatch(setUserDetails(updatedUser));
          if (typeof window !== "undefined") {
            window.localStorage.setItem("user", JSON.stringify(updatedUser));
            window.dispatchEvent(
              new CustomEvent("show-snackbar", { detail: { type: "success", message: "Name updated successfully." } })
            );
          }
        } else {
          setNameError(true);
          const isGoogleUser =
            userdetails?.metadata?.login_method === "google_login" || userdetails?.metadata?.login_method === "google";
          const errMsg = isGoogleUser
            ? "Name is not editable for Google login"
            : "Failed to update name. Please try again.";
          setNameErrorMessage(errMsg);
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("show-snackbar", { detail: { type: "failure", message: errMsg } }));
          }
          console.error("[OnboardModal] Name Update Failed in SSO.", res?.errorMessage);
        }
      })
      .catch((err) => {
        setIsUpdatingName(false);
        setNameError(true);
        const isGoogleUser =
          userdetails?.metadata?.login_method === "google_login" || userdetails?.metadata?.login_method === "google";
        const errMsg = isGoogleUser
          ? "Name is not editable for Google login"
          : "Failed to update name. Please try again.";
        setNameErrorMessage(errMsg);
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("show-snackbar", { detail: { type: "failure", message: errMsg } }));
        }
        console.error("[OnboardModal] Name Update Failed in SSO.", err);
      });
  };

  const handleMobileChange = (e) => {
    const val = e.target.value;
    setMobileNumber(val);
    if (mobileError) {
      setMobileError(false);
      setMobileErrorMessage("");
    }

    if (val === savedMobile && savedMobile) {
      setOtpVerified(true);
      setOtpSent(false);
      setOtpError(false);
    } else {
      setOtpVerified(false);
      setOtpSent(false);
      setOtpValue("");
      setOtpError(false);
      setTimer(30);
    }
  };

  const handleMobileBlur = () => {
    if (!mobileNumber || mobileNumber.trim().length === 0) {
      if (!email || email.trim().length === 0) {
        setMobileError(true);
        setMobileErrorMessage("Either mobile number or email is required");
      } else {
        setMobileError(false);
        setMobileErrorMessage("");
      }
    } else if (!/^\d{10}$/.test(mobileNumber)) {
      setMobileError(true);
      setMobileErrorMessage("Enter a valid 10-digit indian mobile number");
    } else {
      setMobileError(false);
      setMobileErrorMessage("");
    }
  };

  const isSendOtpDisabled = mobileNumber === savedMobile || isVerifying;

  const handleSendOtp = () => {
    if (isSendOtpDisabled) return;

    if (!/^\d{10}$/.test(mobileNumber)) {
      setMobileError(true);
      setMobileErrorMessage("Enter a valid 10-digit indian mobile number");
      return;
    }

    sendChangePhoneOtp(`+91${mobileNumber}`, window.location.href)
      .then((res) => {
        if (res?.preAuthSessionId && res?.deviceId) {
          setAuthSessionInfo({ preAuthSessionId: res.preAuthSessionId, deviceId: res.deviceId });
          setOtpValue("");
          setOtpSent(true);
          setTimer(30);
          setMobileError(false);
          setMobileErrorMessage("");
          if (typeof window !== "undefined") {
            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent("show-snackbar", {
                  detail: { type: "info", message: "OTP has been sent to your Mobile Number" }
                })
              );
            }, 100);
          }
        } else {
          setMobileError(true);
          setMobileErrorMessage(res?.errorMessage || "Failed to send OTP.");
        }
      })
      .catch((err) => {
        setMobileError(true);
        setMobileErrorMessage(err?.errorMessage || "Failed to send OTP.");
      });
  };

  const handleResendOtp = () => {
    if (timer === 0) {
      sendChangePhoneOtp(`+91${mobileNumber}`, window.location.href)
        .then((res) => {
          if (res?.preAuthSessionId && res?.deviceId) {
            setAuthSessionInfo({ preAuthSessionId: res.preAuthSessionId, deviceId: res.deviceId });
            setOtpValue("");
            setTimer(30);
            setOtpError(false);
            if (typeof window !== "undefined") {
              setTimeout(() => {
                window.dispatchEvent(
                  new CustomEvent("show-snackbar", {
                    detail: { type: "info", message: "OTP has been sent to your Mobile Number" }
                  })
                );
              }, 100);
            }
          }
        })
        .catch(() => setOtpError(true));
    }
  };

  const handleVerifyOtp = (otpToVerify) => {
    const code = typeof otpToVerify === "string" ? otpToVerify : otpValue;
    if (isVerifying || code.length !== 6 || !authSessionInfo.preAuthSessionId) return;
    setIsVerifying(true);

    verifySsoCode(authSessionInfo.preAuthSessionId, authSessionInfo.deviceId, code)
      .then((res) => {
        setIsVerifying(false);
        if (res?.status === "OK") {
          setOtpError(false);
          setOtpVerified(true);
          setSavedMobile(mobileNumber);
          setOtpSent(false);

          const updatedUser = {
            ...userdetails,
            userData: { ...userdetails?.userData, phoneNumbers: [`+91${mobileNumber}`] }
          };
          dispatch(setUserDetails(updatedUser));
          if (typeof window !== "undefined") {
            window.localStorage.setItem("user", JSON.stringify(updatedUser));
            window.dispatchEvent(
              new CustomEvent("show-snackbar", {
                detail: { type: "success", message: "Mobile number updated successfully." }
              })
            );
          }
        } else {
          setOtpError(true);
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("show-snackbar", {
                detail: { type: "failure", message: "Failed to update mobile number. Please try again." }
              })
            );
          }
          console.warn("[OnboardModal] Mobile OTP Verification Failed:", res);
        }
      })
      .catch((err) => {
        setIsVerifying(false);
        setOtpError(true);
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("show-snackbar", {
              detail: { type: "failure", message: "Failed to update mobile number. Please try again." }
            })
          );
        }
        console.error("[OnboardModal] Mobile Number Update Failed in SSO.", err);
      });
  };

  const handleOtpChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtpValue(val);
    if (otpError) setOtpError(false);
    if (val.length === 6) {
      handleVerifyOtp(val);
    }
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    if (emailError) {
      setEmailError(false);
      setEmailErrorMessage("");
    }

    if (val === savedEmail && savedEmail) {
      setEmailOtpVerified(true);
      setEmailOtpSent(false);
    } else {
      setEmailOtpVerified(false);
      setEmailOtpSent(false);
      setEmailOtpValue("");
      setEmailTimer(30);
    }
  };

  const handleEmailBlur = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || email.trim().length === 0) {
      if (!mobileNumber || mobileNumber.trim().length === 0) {
        setEmailError(true);
        setEmailErrorMessage("Either mobile number or email is required");
      } else {
        setEmailError(false);
        setEmailErrorMessage("");
      }
    } else if (!emailRegex.test(email)) {
      setEmailError(true);
      setEmailErrorMessage("Invalid email address.");
    } else {
      setEmailError(false);
      setEmailErrorMessage("");
    }
  };

  const isSendEmailOtpDisabled = email === savedEmail || isVerifyingEmail;

  const handleSendEmailOtp = () => {
    if (isSendEmailOtpDisabled) return;

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(true);
      setEmailErrorMessage("Invalid email address.");
      return;
    }

    sendChangeEmailOtp(email, window.location.href)
      .then((res) => {
        if (res?.preAuthSessionId && res?.deviceId) {
          setAuthSessionInfo({ preAuthSessionId: res.preAuthSessionId, deviceId: res.deviceId });
          setEmailOtpValue("");
          setEmailOtpSent(true);
          setEmailTimer(30);
          setEmailError(false);
          setEmailErrorMessage("");
          if (typeof window !== "undefined") {
            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent("show-snackbar", { detail: { type: "info", message: "OTP has been sent to your Email ID" } })
              );
            }, 100);
          }
        } else {
          setEmailError(true);
          setEmailErrorMessage(res?.errorMessage || "Failed to send OTP.");
        }
      })
      .catch((err) => {
        setEmailError(true);
        setEmailErrorMessage(err?.errorMessage || "Failed to send OTP.");
      });
  };

  const handleResendEmailOtp = () => {
    if (emailTimer === 0) {
      sendChangeEmailOtp(email, window.location.href)
        .then((res) => {
          if (res?.preAuthSessionId && res?.deviceId) {
            setAuthSessionInfo({ preAuthSessionId: res.preAuthSessionId, deviceId: res.deviceId });
            setEmailOtpValue("");
            setEmailTimer(30);
            setEmailOtpError(false);
            if (typeof window !== "undefined") {
              setTimeout(() => {
                window.dispatchEvent(
                  new CustomEvent("show-snackbar", { detail: { type: "info", message: "OTP has been sent to your Email ID" } })
                );
              }, 100);
            }
          }
        })
        .catch(() => setEmailOtpError(true));
    }
  };

  const handleVerifyEmailOtp = (otpToVerify) => {
    const code = typeof otpToVerify === "string" ? otpToVerify : emailOtpValue;
    if (isVerifyingEmail || code.length !== 6 || !authSessionInfo.preAuthSessionId) return;
    setIsVerifyingEmail(true);

    verifySsoCode(authSessionInfo.preAuthSessionId, authSessionInfo.deviceId, code)
      .then((res) => {
        setIsVerifyingEmail(false);
        if (res?.status === "OK") {
          setEmailOtpError(false);
          setEmailOtpVerified(true);
          setSavedEmail(email);
          setEmailOtpSent(false);

          const updatedUser = { ...userdetails, userData: { ...userdetails?.userData, emails: [email] } };
          dispatch(setUserDetails(updatedUser));
          if (typeof window !== "undefined") {
            window.localStorage.setItem("user", JSON.stringify(updatedUser));
            window.dispatchEvent(
              new CustomEvent("show-snackbar", {
                detail: { type: "success", message: "Email ID updated successfully." }
              })
            );
          }
        } else {
          setEmailOtpError(true);
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("show-snackbar", {
                detail: { type: "failure", message: "Failed to update email ID. Please try again." }
              })
            );
          }
          console.warn("[OnboardModal] Email OTP Verification Failed:", res);
        }
      })
      .catch((err) => {
        setIsVerifyingEmail(false);
        setEmailOtpError(true);
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("show-snackbar", {
              detail: { type: "failure", message: "Failed to update email ID. Please try again." }
            })
          );
        }
        console.error("[OnboardModal] Email ID Update Failed in SSO.", err);
      });
  };

  const handleEmailOtpChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setEmailOtpValue(val);
    if (emailOtpError) setEmailOtpError(false);
    if (val.length === 6) {
      handleVerifyEmailOtp(val);
    }
  };

  const handleStart = () => {
    if (!isFormComplete || isSubmittingOnboarding) return;

    setIsSubmittingOnboarding(true);
    putUserDetails({
      tpml_onboarding_completed: true,
      tpml_onboarding_completed_at: new Date().toISOString()
    })
      .then((res) => {
        if (res && !res.errorMessage) {
          const updatedUser = {
            ...userdetails,
            metadata: {
              ...userdetails?.metadata,
              tpml_onboarding_completed: true,
              tpml_onboarding_completed_at: new Date().toISOString()
            }
          };
          dispatch(setUserDetails(updatedUser));
          if (typeof window !== "undefined") {
            window.localStorage.setItem("user", JSON.stringify(updatedUser));
            onClose();
          }
        } else {
          setIsSubmittingOnboarding(false);
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("show-snackbar", {
                detail: { type: "failure", message: "Failed to save onboarding details. Please try again." }
              })
            );
          }
        }
      })
      .catch((err) => {
        setIsSubmittingOnboarding(false);
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("show-snackbar", {
              detail: { type: "failure", message: "Failed to save onboarding details. Please try again." }
            })
          );
        }
        console.error("[OnboardModal] Failed to mark onboarding completed:", err);
      });
  };

  return (
    <div styleName="overlay">
      <div styleName="popupContainer" onClick={(e) => e.stopPropagation()}>
        <h2 styleName="title">ಪಿವಿ ಪ್ರೀಮಿಯಂಗೆ ಸ್ವಾಗತ</h2>

        <p styleName="description">
          ನಿಮ್ಮ ಚಂದಾದಾರಿಕೆಯಿಂದ ಹೆಚ್ಚಿನದನ್ನು ಪಡೆಯಿರಿ. ದಯವಿಟ್ಟು ಕೆಲ ಮಾಹಿತಿಯನ್ನು ಹಂಚಿಕೊಂಡಲ್ಲಿ ಕಾಲಕಾಲಕ್ಕೆ ಮಾಹಿತಿ ಹಂಚಿಕೊಳ್ಳಲು
          ಅನುಕೂಲವಾಗಲಿದೆ.
        </p>

        <div styleName="formSection">
          <div styleName="inputGroup">
            <div styleName="inputLabelRow">
              <UserIcon styleName="inputIcon" />
              <span styleName="inputLabel">ಹೆಸರು *</span>
            </div>
            <input
              type="text"
              placeholder="ನಿಮ್ಮ ಹೆಸರು ನಮೂದಿಸಿ"
              styleName="inputField"
              value={name}
              readOnly={isGoogleUser}
              onClick={() => {
                if (isGoogleUser && typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent("show-snackbar", { detail: { type: "failure", message: "Name is not editable for Google login" } }));
                } else {
                  closeOtherEdits("name");
                  if (nameDone) setNameDone(false);
                }
              }}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) {
                  setNameError(false);
                  setNameErrorMessage("");
                }
                if (nameDone) setNameDone(false);
              }}
            />
            <div styleName={nameError ? "inputUnderlineError" : "inputUnderline"} />
            {nameError && !isUpdatingName && (
              <div styleName="errorMessage" style={{ marginTop: "5px" }}>
                {nameErrorMessage}
              </div>
            )}

            {!nameDone && (
              <div styleName="otpButtonContainer" style={{ gap: "10px" }}>
                <button
                  styleName={
                    isUpdatingName ? "nameDoneBtnLoading" : isNameUpdateDisabled ? "nameDoneBtnDisabled" : "nameDoneBtn"
                  }
                  onClick={handleUpdateName}
                  disabled={isNameUpdateDisabled}
                >
                  {isUpdatingName ? (
                    <>
                      <SpinnerIcon styleName="spinner" />
                      ನವೀಕರಿಸಲಾಗುತ್ತಿದೆ
                    </>
                  ) : (
                    "ಪೂರ್ಣಗೊಳಿಸಿ"
                  )}
                </button>
                <button
                  styleName="nameCancelBtn"
                  onClick={() => {
                    setName(savedName || "");
                    setNameDone(true);
                    setNameError(false);
                    setNameErrorMessage("");
                  }}
                >
                  ರದ್ದು
                </button>
              </div>
            )}
          </div>

          <div styleName={otpSent ? "inputGroup inputGroupReducedMargin" : "inputGroup"}>
            <div styleName="inputLabelRow">
              <PhoneIcon styleName="inputIcon" />
              <span styleName="inputLabel">ಮೊಬೈಲ್ ಸಂಖ್ಯೆ</span>
            </div>
            {/* Using a margin bottom on inputField to represent the space even without placeholder */}
            <div styleName="phoneInputContainer" data-has-value={mobileNumber.length > 0}>
              {mobileNumber.length > 0 && <div styleName="countryCode">+91</div>}
              <input
                type="tel"
                placeholder="ನಿಮ್ಮ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ ನಮೂದಿಸಿ"
                styleName="inputField phoneInputField"
                value={mobileNumber}
                onClick={() => {
                  closeOtherEdits("mobile");
                  if (otpVerified) {
                    setOtpVerified(false);
                  }
                }}
                onChange={handleMobileChange}
              />
            </div>
            <div styleName={mobileError && !otpSent ? "inputUnderlineError" : "inputUnderline"} />

            {mobileError && !otpSent && (
              <div styleName="errorMessage">{mobileErrorMessage || "Enter a valid 10-digit indian mobile number"}</div>
            )}

            {!otpVerified && (
              <>
                {otpSent && (
                  <div styleName="otpInputWrapper">
                    <input
                      type="text"
                      placeholder="ಒಟಿಪಿ ದಾಖಲಿಸಿ"
                      styleName="inputField otpInputField"
                      value={otpValue}
                      maxLength={6}
                      onChange={handleOtpChange}
                    />
                    <div styleName={otpError ? "inputUnderlineError" : "inputUnderline"} />
                  </div>
                )}

                {otpSent && otpError && <div styleName="errorMessage">Incorrect OTP. Please try again.</div>}

                <div styleName="otpButtonContainer" style={{ gap: otpSent ? "4px" : "10px" }}>
                  <button
                    styleName={
                      otpSent
                        ? isVerifying
                          ? "otpVerifyBtnLoading"
                          : otpValue.length !== 6
                            ? "otpVerifyBtnDisabled"
                            : "otpVerifyBtn"
                        : isSendOtpDisabled
                          ? "otpResendBtnDisabled"
                          : "otpResendBtn"
                    }
                    onClick={otpSent ? handleVerifyOtp : handleSendOtp}
                    disabled={otpSent ? isVerifying || otpValue.length !== 6 : isSendOtpDisabled}
                  >
                    {otpSent ? (
                      isVerifying ? (
                        <>
                          <SpinnerIcon styleName="spinner" />
                          ಪರಿಶೀಲಿಸಲಾಗುತ್ತಿದೆ
                        </>
                      ) : (
                        "ಒಟಿಪಿ ಪರಿಶೀಲಿಸಿ"
                      )
                    ) : (
                      "ಒಟಿಪಿ ಕಳುಹಿಸಿ"
                    )}
                  </button>
                  <button
                    styleName="otpCancelBtn"
                    onClick={() => {
                      setMobileNumber(savedMobile || "");
                      setMobileError(false);
                      setOtpSent(false);
                      setTimer(30);
                      setOtpError(false);
                      setOtpValue("");
                      setOtpVerified(true);
                    }}
                  >
                    ರದ್ದು
                  </button>
                </div>

                {otpSent && (
                  <div styleName="resendContainer">
                    {timer > 0 ? (
                      <div styleName="resendBlock">
                        <span styleName="resendTextDisabled">ಒಟಿಪಿ ಮತ್ತೊಮ್ಮೆ ಕಳುಹಿಸಿ</span>
                        <div styleName="timerWrapper">
                          <TimerIcon timer={timer} />
                          <span styleName="timerCount">{timer}</span>
                        </div>
                      </div>
                    ) : (
                      <span styleName="resendTextActive" onClick={handleResendOtp}>
                        ಒಟಿಪಿ ಮತ್ತೊಮ್ಮೆ ಕಳುಹಿಸಿ
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div styleName={emailOtpSent ? "inputGroup inputGroupReducedMargin" : "inputGroup"}>
            <div styleName="inputLabelRow">
              <EmailIcon styleName="inputIcon" />
              <span styleName="inputLabel">ಇಮೇಲ್ ವಿಳಾಸ</span>
            </div>
            <input
              type="email"
              placeholder="ನಿಮ್ಮ ಇಮೇಲ್ ವಿಳಾಸ ನಮೂದಿಸಿ"
              styleName="inputField emailInputField"
              value={email}
              readOnly={isGoogleUser}
              onClick={() => {
                if (isGoogleUser && typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent("show-snackbar", { detail: { type: "failure", message: "Email ID is not editable when logged in via Google." } }));
                } else {
                  closeOtherEdits("email");
                  if (emailOtpVerified) {
                    setEmailOtpVerified(false);
                  }
                }
              }}
              onChange={handleEmailChange}
              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
            />
            <div styleName={emailError && !emailOtpSent ? "inputUnderlineError" : "inputUnderline"} />

            {!emailOtpVerified && (
              <>
                {emailOtpSent && (
                  <div styleName="otpInputWrapper">
                    <input
                      type="text"
                      placeholder="ಒಟಿಪಿ ದಾಖಲಿಸಿ"
                      styleName="inputField otpInputField"
                      value={emailOtpValue}
                      maxLength={6}
                      onChange={handleEmailOtpChange}
                    />
                    <div styleName={emailOtpError ? "inputUnderlineError" : "inputUnderline"} />
                  </div>
                )}

                {emailOtpSent && emailOtpError && <div styleName="errorMessage">Incorrect OTP. Please try again.</div>}
                {emailError && !emailOtpSent && (
                  <div styleName="errorMessage">{emailErrorMessage || "Invalid email address."}</div>
                )}

                <div styleName="otpButtonContainer" style={{ gap: emailOtpSent ? "4px" : "10px" }}>
                  <button
                    styleName={
                      emailOtpSent
                        ? isVerifyingEmail
                          ? "otpVerifyBtnLoading"
                          : emailOtpValue.length !== 6
                            ? "otpVerifyBtnDisabled"
                            : "otpVerifyBtn"
                        : isSendEmailOtpDisabled
                          ? "otpResendBtnDisabled"
                          : "otpResendBtn"
                    }
                    onClick={emailOtpSent ? handleVerifyEmailOtp : handleSendEmailOtp}
                    disabled={emailOtpSent ? isVerifyingEmail || emailOtpValue.length !== 6 : isSendEmailOtpDisabled}
                  >
                    {emailOtpSent ? (
                      isVerifyingEmail ? (
                        <>
                          <SpinnerIcon styleName="spinner" />
                          ಪರಿಶೀಲಿಸಲಾಗುತ್ತಿದೆ
                        </>
                      ) : (
                        "ಒಟಿಪಿ ಪರಿಶೀಲಿಸಿ"
                      )
                    ) : (
                      "ಒಟಿಪಿ ಕಳುಹಿಸಿ"
                    )}
                  </button>
                  <button
                    styleName="otpCancelBtn"
                    onClick={() => {
                      setEmail(savedEmail || "");
                      setEmailError(false);
                      setEmailOtpSent(false);
                      setEmailTimer(30);
                      setEmailOtpError(false);
                      setEmailOtpValue("");
                      setEmailOtpVerified(true);
                    }}
                  >
                    ರದ್ದು
                  </button>
                </div>

                {emailOtpSent && (
                  <div styleName="resendContainer">
                    {emailTimer > 0 ? (
                      <div styleName="resendBlock">
                        <span styleName="resendTextDisabled">ಒಟಿಪಿ ಮತ್ತೊಮ್ಮೆ ಕಳುಹಿಸಿ</span>
                        <div styleName="timerWrapper">
                          <TimerIcon timer={emailTimer} />
                          <span styleName="timerCount">{emailTimer}</span>
                        </div>
                      </div>
                    ) : (
                      <span styleName="resendTextActive" onClick={handleResendEmailOtp}>
                        ಒಟಿಪಿ ಮತ್ತೊಮ್ಮೆ ಕಳುಹಿಸಿ
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <button
            styleName={isFormComplete ? "ctaButton ctaButtonActive" : "ctaButton"}
            onClick={handleStart}
            disabled={!isFormComplete || isSubmittingOnboarding}
          >
            {isSubmittingOnboarding ? "ಉಳಿಸಲಾಗುತ್ತಿದೆ..." : "ಪ್ರಾರಂಭಿಸಿ"}
          </button>
        </div>
      </div>
    </div>
  );
};
